import type { MatchSpan } from './types';

/**
 * Filler words that never carry filter intent on their own. Used to decide
 * whether an uncovered span is meaningful residual (and for the short-content
 * null check) — not to strip context from around leftover words.
 */
const FILLER_WORDS = new Set(
  [
    'in',
    'on',
    'or',
    'me',
    'at',
    'near',
    'around',
    'with',
    'the',
    'a',
    'an',
    'of',
    'by',
    'for',
    'please',
    'show',
    'find',
    'i',
    'want',
    'need',
    'looking',
    'some',
    'any',
    'and',
    'to',
    'just',
    'maybe',
    'like',
    'home',
    'homes',
    'property',
    'properties',
    'listing',
    'listings',
    'place',
    'places',
  ].map(w => w.toLowerCase())
);

function isFillerToken(token: string): boolean {
  const t = token.toLowerCase();
  if (FILLER_WORDS.has(t)) return true;
  // Multi-word fillers collapsed after punctuation strip (e.g. "real estate").
  return t === 'real' || t === 'estate';
}

function hasMeaningfulContent(text: string): boolean {
  const content = text
    .split(/\s+/)
    .filter(Boolean)
    .filter(t => !isFillerToken(t))
    .join('');
  return content.length > 3;
}

/**
 * Derive residual text from recorded match spans.
 *
 * Keeps filler words that sit in the same uncovered gap as leftover content
 * (e.g. "and backyard with bbq") so context stays clear. Gaps that are only
 * fillers are dropped. Returns null when no meaningful leftover remains.
 */
export function deriveResidualFromSpans(
  normalizedQuery: string,
  spans: ReadonlyArray<MatchSpan>
): string | null {
  const length = normalizedQuery.length;
  if (length === 0) return null;

  const covered = new Array<boolean>(length).fill(false);
  for (const span of spans) {
    const start = Math.max(0, Math.min(span.start, length));
    const end = Math.max(0, Math.min(span.end, length));
    for (let i = start; i < end; i++) {
      covered[i] = true;
    }
  }

  const parts: string[] = [];
  let i = 0;
  while (i < length) {
    while (i < length && covered[i]) i++;
    if (i >= length) break;
    const rangeStart = i;
    while (i < length && !covered[i]) i++;
    const raw = normalizedQuery
      .slice(rangeStart, i)
      .replace(/[!?;,.]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!raw) continue;
    if (!hasMeaningfulContent(raw)) continue;
    parts.push(raw);
  }

  if (parts.length === 0) return null;
  const residual = parts.join(' ').trim();
  return hasMeaningfulContent(residual) ? residual : null;
}
